<template>
    <div class="chat-panel" :class="{ 'has-search-scope': !props.isDocumentView && $slots['search-context'] }">
        <div class="chat-title ai-card">
            <div>
                <h3>Chat with your research</h3>
                <div class="small-text">AI-powered</div>
            </div>
            <div class="chat-buttons">
                <primevue-button text class="ai-icon" v-tooltip.left="'About our AI chat service'" @click="updateShowAiWarning">
                    <font-awesome-icon :icon="['fas', 'sparkles']" />
                </primevue-button>
                <primevue-button text v-tooltip.left="'New chat'" :disabled="!hasChatted || !props.canClearContext" @click="emit('clearContext')">
                    <font-awesome-icon :icon="['fas', 'message-plus']" />
                </primevue-button>
                <primevue-button text v-tooltip.left="'Chat history'" @click="openChatHistory" v-if="authState.loggedIn">
                    <font-awesome-icon :icon="['fas', 'clock']" />
                </primevue-button>
                <primevue-button
                    v-if="!props.isDocumentView"
                    text
                    class="desktop-only"
                    v-tooltip.left="deltaGptUserSettingsStore.chatExpanded ? 'Collapse chat' : 'Expand chat'"
                    @click="expandChat"
                >
                    <font-awesome-icon :icon="['fas', deltaGptUserSettingsStore.chatExpanded ? 'arrow-right-to-line' : 'arrow-left-to-line']" />
                </primevue-button>
            </div>
        </div>
        <div v-if="!props.isDocumentView && $slots['search-context']" class="search-scope-row">
            Searching in:
            <slot name="search-context" />
        </div>
        <div class="chat" ref="scrollContainer">
            <div class="backdrop" :class="{ chatting: hasChatted }">
                <particle-container :animate="!hasChatted && !props.isDocumentView" />
            </div>
            <div v-if="!hasChatted" class="prompt-holder">
                <p>Chat with your research using the input below or select one of our prompts.</p>
                <div class="chat-prompts" :class="{ locked: props.isLocked }">
                    <slot name="prompts" />
                </div>
            </div>
            <div class="messages-wrapper" v-if="hasChatted">
                <chat-bubble
                    :is-replying="props.isReplying"
                    :key="i"
                    :last-question="messages[i - 1]?.content ?? ''"
                    :message="message.content"
                    :role="message.role"
                    @give-feedback="giveFeedback"
                    @open-feedback-input="openFeedbackInput"
                    v-for="(message, i) in props.messages"
                />
                <div class="search-loader" v-if="props.isAwaitingResponse">
                    Searching your research
                    <dots-loader />
                </div>
                <div class="scroll-to-bottom">
                    <primevue-button
                        class="scroll-button"
                        v-if="!arrivedState.bottom && props.isChatExpanded && hasChatted"
                        @click="scrollToBottom">
                        <font-awesome-icon :icon="['fas', 'arrow-down']" />
                    </primevue-button>
                </div>
                <central-modal
                    header="Feedback"
                    v-model:visible="showFeedbackInput"
                >
                    <div class="feedback-content">
                        <primevue-textarea
                            auto-resize
                            maxlength="500"
                            placeholder="Please provide feedback"
                            v-model="feedbackMessage"
                            rows="5"
                            cols="50"
                        />
                    </div>
                    <template #footer>
                        <div class="feedback-footer">
                            <div>
                                <primevue-button
                                    v-tooltip.bottom="'Good response'"
                                    text
                                    class="feedback-button"
                                    :class="{ 'good-feedback': goodFeedback }"
                                    @click="setFeedback(true)">
                                    <font-awesome-icon :icon="['fas', 'thumbs-up']" />
                                </primevue-button>
                                <primevue-button
                                    v-tooltip.bottom="'Bad response'"
                                    text
                                    class="feedback-button"
                                    :class="{ 'bad-feedback': badFeedback }"
                                    @click="setFeedback(false)">
                                    <font-awesome-icon :icon="['fas', 'thumbs-down']" />
                                </primevue-button>
                            </div>
                            <primevue-button
                                v-if="currentMessageFeedback"
                                label="Submit"
                                :disabled="!goodFeedback && !badFeedback || !feedbackMessageHasText"
                                @click="giveFeedback({ ...currentMessageFeedback, goodResponse: goodFeedback, optionalResponseFeedback: feedbackMessage })" />
                        </div>
                    </template>
                </central-modal>
            </div>
        </div>
        <div v-if="hasChatted" class="actions-bar">
            <slot name="actions" />
            <div />

        </div>
        <div ref="inputRow" class="input-row" :class="{ locked: props.isLocked }">
            <input-group>
                <div class="chat-input-wrapper">
                    <primevue-textarea
                        :disabled="props.isAwaitingResponse || props.isReplying"
                        @keyup.prevent.enter="sendMessage()"
                        class="chat-input"
                        id="chat"
                        :maxlength="maxChatMessageLength"
                        :placeholder="placeholderChatText"
                        type="text"
                        v-model="chatMessage"
                    />
                    <div class="chat-limit" :class="{ warning: remainingChatCharacters <= chatLimitWarningThreshold }">
                        {{ remainingChatCharacters }} characters left
                    </div>
                </div>
                <input-group-addon>
                    <primevue-button text :disabled="isChatDisabled" @click="sendMessage()">
                        <font-awesome-icon :icon="['fas', 'paper-plane']" />
                    </primevue-button>
                </input-group-addon>
            </input-group>
        </div>
        <div class="lock" v-if="props.isLocked">
            <font-awesome-icon :icon="['fas', 'lock-keyhole']" />
        </div>
        <central-modal
            v-model:visible="showChatHistory"
            header="Chat History"
            size="medium"
        >
            <chat-history :conversation-history="props.conversationHistory" @close="closeChatHistory" />
        </central-modal>
        <central-modal
            @update:visible="acceptAiWarning"
            header="About our AI chat service"
            size="medium"
            v-model:visible="showAiWarning"
        >
            <div class="welcome-drawer-content">
                <p>Our AI service enables you to chat with your research, as well as converting it to a video or podcast, and in different languages.</p>
                <h4>Content Access</h4>
                <p>You will only be able to see content published by the LCP Delta research services you are subscribed to. Note that our AI service currently only searches through PDF reports, and does not query any spreadsheets or Power BI reports you may have access to on the full <a href="https://research.lcpdelta.com/" target="_blank" rel="noopener noreferrer">Subscriber Portal</a>.</p>
                <p>When asking a question on the Documents page, the answer will be generated from all of the documents you have filtered down to. Note that answers will be constructed from all reports in the filtered list - which may belong to different services, and may have been published at different times. Always check the sources of the answer.</p>
                <h4>Tips</h4>
                <ul>
                    <li>
                        If you are chatting with a document and now want to ask an unrelated question to your previous questions, it is better to start a new chat.
                    </li>
                    <li>
                        Our AI service will automatically respond in the language of the question asked. Alternatively, you can ask it to respond in a different language also.
                    </li>
                </ul>
                <h4>Important Notes</h4>
                <p>These AI Services are : </p>
                <ol>
                    <li>
                        made available in preview mode only and
                    </li>
                    <li>
                        use artificial intelligence and machine learning to assist in the generation of outputs.
                    </li>
                </ol>
                <p>
                    Although reasonable measures are taken to improve the accuracy of the outputs, they are produced without human oversight, may contain errors and should not be relied on.<br />  Please do not input personal data or confidential information into the AI Services. You must read and agree to the full terms of use before using the AI Services.
                </p>
            </div>
        </central-modal>
        <contact-form v-model:visible="showContactDialog" />
    </div>
</template>

<script lang="ts" setup>
import PrimevueButton from 'primevue/button';
import PrimevueTextarea from 'primevue/textarea';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import ParticleContainer from '@/components/deltaGpt/Crosshairs/ParticleContainer.vue';
import ContactForm from '@/components/deltaGpt/ContactForm.vue';

import {
    computed, onBeforeUnmount, onMounted, onUnmounted, ref,
} from 'vue';
import { useScroll } from '@vueuse/core';
import { useToast } from 'primevue/usetoast';
import ChatBubble from '@/components/deltaGpt/ChatBubble.vue';
import { IFeedback } from '@/interfaces/deltaGpt/IFeedback.ts';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { publishToast } from '@/utils/Toast.ts';
import { IChatMessage } from '@/interfaces/deltaGpt/IChatMessage.ts';
import { IConversationSummary } from '@/interfaces/deltaGpt/IConversationSummary.ts';
import { useDeltaGptHistoryStore } from '@/stores/deltaGpt/deltaGptHistoryStore.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { authState } from '@lcp/http-auth';
import { useDeltaUserSettingsStore } from '@/stores/deltaGpt/deltaGptUserSettingsStore.ts';
import DotsLoader from '@/components/layout/DotsLoader.vue';
import CentralModal from '../common/CentralModal.vue';
import ChatHistory from './ChatHistory.vue';

const toast = useToast();
const userAccessStore = useUserAccessStore();
const deltaGptUserSettingsStore = useDeltaUserSettingsStore();

const props = defineProps<{
    messages: Array<IChatMessage>;
    isAwaitingResponse: boolean;
    isReplying: boolean;
    conversationHistory?: Record<string, IConversationSummary>;
    isChatExpanded?: boolean;
    canExpandChat: boolean;
    isLocked?: boolean;
    canClearContext?: boolean;
    isDocumentView?: boolean;
}>();

const emit = defineEmits<{ sendMessage: [string, string?]; sourceClicked: [string]; clearContext: [] }>();

const deltaGptHistoryStore = useDeltaGptHistoryStore();

const scrollContainer = ref<HTMLElement | null>(null);
const chatMessage = ref<string>('');
const showFeedbackInput = ref<boolean>(false);
const feedbackMessage = ref<string>('');
const currentMessageFeedback = ref<{ question: string; response: string } | null>(null);
const goodFeedback = ref<boolean>(false);
const badFeedback = ref<boolean>(false);
const showAiWarning = ref<boolean>(false);
const savedAiWarning = ref<boolean>(false);
const showChatHistory = ref<boolean>(false);
const showContactDialog = ref<boolean>(false);
const maxChatMessageLength = 4000;
const chatLimitWarningThreshold = 250;

const isChatDisabled = computed<boolean>(() => !chatMessage.value?.trim() || props.isAwaitingResponse || props.isReplying);
const hasChatted = computed(() => props.messages.length > 0);
const feedbackMessageHasText = computed<boolean>(() => feedbackMessage.value?.trim() !== '');
const placeholderChatText = computed<string>(() => ((props.isAwaitingResponse || props.isReplying) ? '' : 'Ask a question'));
const remainingChatCharacters = computed<number>(() => maxChatMessageLength - chatMessage.value.length);

const {
    arrivedState, y,
} = useScroll(scrollContainer, { behavior: 'smooth' });

function setFeedback (isGood: boolean): void {
    if (isGood) {
        goodFeedback.value = true;
        badFeedback.value = false;
    } else {
        goodFeedback.value = false;
        badFeedback.value = true;
    }
}

function giveFeedback (feedback: IFeedback): void {
    deltaGptApi.postAiResponseFeedback(feedback).then(() => {
        showFeedbackInput.value = false;
        feedbackMessage.value = '';
        currentMessageFeedback.value = null;
        goodFeedback.value = false;
        badFeedback.value = false;

        publishToast(toast, 'Feedback submitted', 'success');
    });
}

function openFeedbackInput (feedback: { question: string; response: string }): void {
    showFeedbackInput.value = true;
    currentMessageFeedback.value = feedback;
}

function sendMessage (promptText: string | null = null): void {
    if (!hasChatted.value) deltaGptHistoryStore.currentChatHistoryId = null;
    emit('sendMessage', promptText || chatMessage.value);
    chatMessage.value = '';
}

async function onSourceClicked (event: MouseEvent): Promise<void> {
    const target = event.target as HTMLElement;
    if (target.dataset.reference) {
        emit('sourceClicked', target.dataset.reference);
    }
}

function scrollToBottom (): void {
    const scrollHeight = scrollContainer.value?.scrollHeight;
    if (scrollHeight) y.value += scrollHeight;
    arrivedState.bottom = true;
}

function openChatHistory (): void {
    showChatHistory.value = true;
}

function expandChat (): void {
    deltaGptUserSettingsStore.chatExpanded = !deltaGptUserSettingsStore.chatExpanded;
}

function closeChatHistory (): void {
    showChatHistory.value = false;
}

function updateShowAiWarning (): void {
    showAiWarning.value = true;
    savedAiWarning.value = true;
}

function acceptAiWarning (): void {
    if (!savedAiWarning.value) {
        userAccessStore.acceptAiWarning();
    }
    showAiWarning.value = false;
}

function initialise (): void {
    if (!authState.value.loggedIn) return;

    showAiWarning.value = !userAccessStore.userAccess.research.acceptedAiWarning;
    savedAiWarning.value = userAccessStore.userAccess.research.acceptedAiWarning;
}

window.addEventListener('click', onSourceClicked);

onBeforeUnmount(() => {
    window.removeEventListener('click', onSourceClicked);
});

// Add global function to handle contact button clicks
onMounted(() => {
    (window as unknown as { showContactDialog: () => void }).showContactDialog = (): void => {
        showContactDialog.value = true;
    };
});

onUnmounted(() => {
    delete (window as unknown as { showContactDialog?: () => void }).showContactDialog;
});

initialise();
</script>

<style lang="scss" scoped>
.welcome-drawer-content {
    padding: var(--extraLargeSpacing);
    margin: 0 var(--defaultSpacing);
    display: flex;
    flex-direction: column;
    gap: var(--defaultSpacing);

    h4 {
        margin-top: var(--defaultSpacing);
    }
}

.card {
    background: var(--neutralBackdrop);
    backdrop-filter: blur(10px);
    border-left: 1px solid var(--neutral400);
}

h2 {
    font-weight: 600;
}

ul {
    list-style-type: disc;
    list-style-position: inside;
}

li {
    margin-bottom: var(--defaultSpacing);
}

ol {
    list-style-type: lower-roman;
    list-style-position: inside;
}

.chat-panel {
    border-radius: initial;
    box-shadow: unset;
    display: grid;
    grid-template-rows: auto 1fr auto;
    overflow: hidden;
    padding: 0;
    position: relative;
    height: 100%;
    width: 100%;
    gap: var(--defaultSpacing);
    border-bottom-right-radius: var(--p-dialog-border-radius);
    border-bottom-left-radius: var(--p-dialog-border-radius);

    &.has-search-scope {
        grid-template-rows: auto auto 1fr auto;
    }

    &.locked {
        pointer-events: none;
        filter: blur(5px);
    }

    label {
        position: absolute;
        left: 30px;
        pointer-events: none;
        opacity: 0.5;
    }

    .chat-title {
        align-items: center;
        border-bottom: 1px solid var(--neutral300);
        display: flex;
        justify-content: space-between;
        padding: var(--largeSpacing) var(--largeSpacing) var(--defaultSpacing) var(--largeSpacing);
        gap: var(--defaultSpacing);

        .chat-buttons{
            display: flex;
            gap: var(--defaultSpacing);

            .ai-icon {
                font-size: var(--smallFontSize);
                font-weight: 600;
            }
        }

        .small-text {
            font-size: var(--extraSmallFontSize);
        }
    }
    .prompt-holder {
        margin-top: auto;
        margin-bottom: var(--defaultSpacing);
        display: flex;
        flex-direction: column;
        gap: var(--largeSpacing);

        .chat-prompts {
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: auto;
            gap: var(--defaultSpacing);

            &.locked {
                pointer-events: none;
                filter: blur(5px);
            }
        }

        p {
            color: var(--neutral900);
        }
    }
    .chat {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: var(--smallSpacing);
        overflow: auto;
        padding: 0 var(--extraLargeSpacing);
        margin: 0;
        position: relative;

        .messages-wrapper {
            min-height: auto;
            padding-top: var(--smallSpacing);
            flex: 1;
            display: flex;
            flex-direction: column;
            list-style: none;
            gap: var(--defaultSpacing);
            padding-bottom: var(--defaultSpacing);
        }

        .backdrop {
            display: flex;
            flex-direction: column;
            flex: 1;
            inset: 0;
            align-items: center;
            opacity: 0.6;

            @include responsiveHeight('sm') {
                display: none;
            }

            &.chatting {
                position: absolute;
                top:0;
                left:0;
            }

            img {
                width: 50%;
                height: 50%;
            }

            .title {
                font-size: var(--extraLargeFontSize);
                margin-bottom: var(--smallSpacing);
            }
        }

        .scroll-to-bottom {
            width: 100%;
            display: flex;
            justify-content: center;
            position: sticky;
            bottom: 5px;
            z-index: 10;

            .scroll-button {
                font-size: var(--extraSmallFontSize);
                border: 2px solid var(--neutral300);
                padding: 2px 4px;

                &:hover {
                    box-shadow: none;
                }

                &:focus {
                    box-shadow: none;
                }
            }
    }

}

    .actions-bar {
        width: 100%;
        display: flex;
        justify-content: space-between;
        position: sticky;
        padding: 0 var(--largeSpacing);
        gap: var(--defaultSpacing);
    }

    .lock {
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        bottom: 0;
        font-size: var(--extraLargeFontSize);
        display: flex;
        justify-content: center;
        align-items: center;
    }
}

.input-row {
    align-items: center;
    display: flex;
    gap: 5px;
    justify-content: center;
    max-height: 200px;
    min-height: 60px;
    padding: var(--defaultSpacing) var(--largeSpacing) var(--largeSpacing) var(--largeSpacing);
    position: relative;

    &.locked {
        pointer-events: none;
        filter: blur(5px);
    }

    label {
        position: absolute;
        left: 30px;
        pointer-events: none;
        opacity: 0.8;
    }

    &:focus-within {
        label {
            display: none;
        }
    }

    .chat-input-wrapper {
        display: flex;
        flex: 1 1 auto;
        position: relative;
    }

    .chat-input {
        height: 60px;
        max-height: 200px;
        padding-bottom: calc(var(--largeSpacing) + var(--defaultSpacing));
        padding-top: var(--largeSpacing);
        resize: none;
        width: 100%;
    }

    .chat-limit {
        bottom: var(--smallSpacing);
        color: var(--neutral700);
        font-size: var(--extraSmallFontSize);
        line-height: 1;
        pointer-events: none;
        position: absolute;
        right: var(--defaultSpacing);
        z-index: 11;

        &.warning {
            color: var(--dangerRed);
        }
    }

}

.feedback-content {
    padding: var(--defaultSpacing);
}

.feedback-footer {
    display: flex;
    justify-content: space-between;
    width: 100%;
    gap: var(--defaultSpacing);

    .feedback-button {
        color: var(--primaryFont);

        &:hover {
            background: transparent;
        }
    }

    .good-feedback {
        color: var(--successGreen);
    }

    .bad-feedback {
        color: var(--dangerRed);
    }
}

.search-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--defaultSpacing);
    margin-top: var(--defaultSpacing);
}

.search-scope-row {
    align-items: center;
    border-bottom: 1px solid var(--neutral300);
    display: flex;
    flex-wrap: wrap;
    gap: var(--smallSpacing);
    min-width: 0;
    padding: 0 var(--largeSpacing) var(--defaultSpacing) var(--largeSpacing);
}
</style>
